import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  SelectChangeEvent,
  Autocomplete,
  Alert,
  Chip,
} from '@mui/material';
import { AutoMode, Info } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useDataProvider, useNotify, useGetList, useRefresh } from 'react-admin';
import { tenantService } from '../../services/tenantService';

interface ReminderType {
  id: string;
  name: string;
  color?: string;
  icon?: string;
  description?: string;
  defaultDaysBefore?: number;
  category?: string;
  targetType: 'equipment' | 'user';
}

interface User {
  id: number;
  name: string;
  email: string;
}

interface Equipment {
  id: number;
  name: string;
  make: string;
  model: string;
}

interface ReminderDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId?: number;
  responsibleUserId?: number;
  userId?: number;
  recordId?: number | null;
  isEdit?: boolean;
}

export const ReminderDialog: React.FC<ReminderDialogProps> = ({
  open,
  onClose,
  equipmentId,
  responsibleUserId,
  userId,
  recordId,
  isEdit = false,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  
  const [formData, setFormData] = useState({
    notes: '',
    dueDate: null as Date | null,
    reminderTypeId: '',
    vehicleId: equipmentId || '',
    assignedUserId: '',
    relatedUserId: userId || '',
    status: 'pending',
  });

  const [isAutoGenerated, setIsAutoGenerated] = useState(false);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);

  const [allReminderTypes, setAllReminderTypes] = useState<ReminderType[]>([]);
  const [reminderTypes, setReminderTypes] = useState<ReminderType[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch all reminder types once
  useEffect(() => {
    const fetchReminderTypes = async () => {
      try {
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const tenantCode = tenantService.getCurrentTenantCode();

        const response = await fetch(`${baseUrl}/reminders/types`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'x-tenant-code': tenantCode,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Fetched reminder types:', data);
          setAllReminderTypes(data);
        } else {
          console.error('Failed to fetch reminder types:', response.status);
          const errorText = await response.text();
          console.error('Error response:', errorText);
        }
      } catch (error) {
        console.error('Failed to fetch reminder types:', error);
      }
    };

    if (open) {
      fetchReminderTypes();
    }
  }, [open]);

  // Filter reminder types based on selected user and equipment
  useEffect(() => {
    const hasUser = Boolean(formData.relatedUserId);
    const hasEquipment = Boolean(formData.vehicleId);

    let filteredTypes = [...allReminderTypes];

    if (hasUser && hasEquipment) {
      // Show all types when both are selected
      filteredTypes = allReminderTypes;
    } else if (hasUser && !hasEquipment) {
      // Show only user reminder types
      filteredTypes = allReminderTypes.filter(type => type.targetType === 'user');
    } else if (!hasUser && hasEquipment) {
      // Show only equipment reminder types
      filteredTypes = allReminderTypes.filter(type => type.targetType === 'equipment');
    } else {
      // Show all types when neither is selected (let user choose)
      filteredTypes = allReminderTypes;
    }

    setReminderTypes(filteredTypes);

    // Clear reminder type selection if current selection is no longer valid
    if (formData.reminderTypeId && !filteredTypes.find(type => type.id === formData.reminderTypeId)) {
      setFormData(prev => ({ ...prev, reminderTypeId: '' }));
    }
  }, [allReminderTypes, formData.relatedUserId, formData.vehicleId, formData.reminderTypeId]);

  // Fetch users - sorted by name on server side
  const { data: usersData } = useGetList('users', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  // Fetch equipment
  const { data: equipmentData } = useGetList('equipment', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  useEffect(() => {
    if (usersData) {
      // Ensure users are sorted alphabetically by name (fallback sorting)
      const sortedUsers = [...usersData].sort((a, b) => {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
      setUsers(sortedUsers);
    }
  }, [usersData]);

  useEffect(() => {
    if (equipmentData) setEquipment(equipmentData);
  }, [equipmentData]);

  // Load existing reminder data for editing
  useEffect(() => {
    const loadReminderData = async () => {
      if (isEdit && recordId && open) {
        try {
          const response = await dataProvider.getOne('reminders', { id: recordId });
          const reminder = response.data;
          setFormData({
            notes: reminder.notes || '',
            dueDate: reminder.reminderDate ? new Date(reminder.reminderDate) : null,
            reminderTypeId: reminder.reminderTypeId || '',
            vehicleId: reminder.vehicleId || equipmentId || '',
            assignedUserId: reminder.responsibleUserId || '',
            relatedUserId: reminder.userId || userId || '',
            status: reminder.status || 'pending',
          });
          setIsAutoGenerated(reminder.autoGenerated || false);
          setExpirationDate(reminder.expirationDate ? new Date(reminder.expirationDate) : null);
        } catch (error) {
          console.error('Failed to load reminder:', error);
          notify('Failed to load reminder data', { type: 'error' });
        }
      }
    };

    loadReminderData();
  }, [isEdit, recordId, open, dataProvider, notify, equipmentId]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    handleInputChange(name, value);
  };

  const handleSubmit = async () => {
    if (!formData.dueDate || !formData.reminderTypeId) {
      notify('Please fill in all required fields', { type: 'error' });
      return;
    }

    // Validate reminder type compatibility
    const selectedReminderType = allReminderTypes.find(type => type.id === formData.reminderTypeId);
    if (selectedReminderType) {
      const hasUser = Boolean(formData.relatedUserId);
      const hasEquipment = Boolean(formData.vehicleId);

      if (selectedReminderType.targetType === 'user' && !hasUser) {
        notify('Please select a user for this user-specific reminder type', { type: 'error' });
        return;
      }

      if (selectedReminderType.targetType === 'equipment' && !hasEquipment) {
        notify('Please select equipment for this equipment-specific reminder type', { type: 'error' });
        return;
      }
    }

    setLoading(true);
    
    try {
      const reminderData = {
        reminderTypeId: formData.reminderTypeId,
        vehicleId: formData.vehicleId ? Number(formData.vehicleId) : undefined,
        userId: formData.relatedUserId ? Number(formData.relatedUserId) : (userId || undefined),
        responsibleUserId: formData.assignedUserId ? Number(formData.assignedUserId) : undefined,
        reminderDate: formData.dueDate?.toISOString(),
        notes: formData.notes || undefined,
        status: formData.status,
        tenantCode: tenantService.getCurrentTenantCode(),
      } as any;

      if (isEdit && recordId) {
        await dataProvider.update('reminders', {
          id: recordId,
          data: reminderData,
          previousData: formData || {}
        });
        notify('Reminder updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('reminders', {
          data: reminderData,
        });
        notify('Reminder created successfully', { type: 'success' });
      }

      refresh();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Failed to save reminder:', error);
      notify('Failed to save reminder', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      notes: '',
      dueDate: null,
      reminderTypeId: '',
      vehicleId: equipmentId || '',
      assignedUserId: '',
      relatedUserId: userId || '',
      status: 'pending',
    });
    setIsAutoGenerated(false);
    setExpirationDate(null);
  };

  const handleClose = () => {
    onClose();
    // Don't reset form immediately to avoid clearing data for next edit
    setTimeout(() => {
      if (!isEdit) {
        resetForm();
      }
    }, 300);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEdit ? 'Edit Reminder' : 'Create New Reminder'}
          {isAutoGenerated && (
            <Chip
              icon={<AutoMode sx={{ fontSize: 14 }} />}
              label="Auto"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.75rem',
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
              }}
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {isAutoGenerated && (
            <Alert severity="info" icon={<Info />}>
              This reminder is automatically generated based on expiration dates.
              The reminder date is managed automatically, but you can update the status and assignment.
            </Alert>
          )}

          {expirationDate && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Actual Expiration Date
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {expirationDate.toLocaleDateString()}
              </Typography>
            </Box>
          )}

          <TextField
            label="Description"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            disabled={isAutoGenerated}
            helperText={isAutoGenerated ? "Description is auto-generated" : ""}
          />

          <DatePicker
            label="Due Date *"
            value={formData.dueDate}
            onChange={(date) => handleInputChange('dueDate', date)}
            disabled={isAutoGenerated}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
                helperText: isAutoGenerated ? "Date is automatically managed" : "",
              },
            }}
          />

          {isEdit && (
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleSelectChange}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          )}

          <Autocomplete
            fullWidth
            options={users}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={users.find(u => u.id === formData.relatedUserId) || null}
            onChange={(event, newValue) => {
              handleInputChange('relatedUserId', newValue?.id || '');
            }}
            renderInput={(params) => (
              <TextField {...params} label="User (Optional)" placeholder="Select a user..." />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />

          <Autocomplete
            fullWidth
            clearOnEscape
            clearIcon={<div>×</div>}
            options={equipment}
            getOptionLabel={(option) => `${option.name} - ${option.make} ${option.model}`}
            value={equipment.find(e => e.id === formData.vehicleId) || null}
            onChange={(event, newValue) => {
              handleInputChange('vehicleId', newValue?.id || '');
            }}
            renderInput={(params) => (
              <TextField {...params} label="Equipment (Optional)" placeholder="Select equipment..." />
            )}
            noOptionsText="No equipment found"
            clearText="Clear selection"
          />

          <FormControl fullWidth disabled={isAutoGenerated}>
            <InputLabel>Reminder Type *</InputLabel>
            <Select
              name="reminderTypeId"
              value={formData.reminderTypeId}
              onChange={handleSelectChange}
              label="Reminder Type *"
            >
              {reminderTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: type.color,
                      }}
                    />
                    <Typography>{type.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {isAutoGenerated && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Type is automatically determined
              </Typography>
            )}
          </FormControl>

          <Autocomplete
            fullWidth
            options={users}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={users.find(u => u.id === formData.assignedUserId) || null}
            onChange={(event, newValue) => {
              handleInputChange('assignedUserId', newValue?.id || '');
            }}
            renderInput={(params) => (
              <TextField {...params} label="Responsible User (Optional)" />
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};